import type { Campaign, Variant } from '~/modules/campaign/types';

import { isVariantProductVariantsDifferent } from '~/modules/campaign';

const removeKeys = (obj: any, keysToRemove: string[]): any => {
  if (!obj || typeof obj !== 'object') return obj;

  if (Array.isArray(obj)) {
    return obj.map((item) => removeKeys(item, keysToRemove));
  }

  const newObj: any = {};

  Object.keys(obj).forEach((key) => {
    const fullPath = key;

    if (!keysToRemove.includes(fullPath)) {
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        newObj[key] = removeKeys(obj[key], keysToRemove);
      } else {
        newObj[key] = obj[key];
      }
    }
  });

  return newObj;
};

export const areObjectsDifferent = (obj1: any, obj2: any, keysToRemove: string[] = []): boolean => {
  const cleanObj1 = removeKeys(obj1, keysToRemove);
  const cleanObj2 = removeKeys(obj2, keysToRemove);

  return JSON.stringify(cleanObj1) !== JSON.stringify(cleanObj2);
};

export const UNORDERED_ARRAY_FIELDS: (keyof Campaign)[] = [
  'targetChannels',
  'targetCountries',
  'targetDevices',
  'targetVisitorTypes',
];

export const UNORDERED_ARRAY_CONDITION_FIELDS: (keyof NonNullable<Campaign['conditions']>)[] = [
  'targetMarkets',
  'targetPageIds',
];

export const areArraysSameValues = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return JSON.stringify(sortedA) === JSON.stringify(sortedB);
};

// These targets are implicitly "all" until narrowed, so an unset field and `['all']` mean the same thing.
// The advance-setting form hydrates unset ones to `['all']`, which must not read as a change.
const ALL_TARGET_SENTINEL_FIELDS: string[] = [...UNORDERED_ARRAY_FIELDS, 'targetMarkets'];

const normalizeArrayField = (field: string, value: string[] | undefined): string[] => {
  const isAllTarget = !value?.length || (value.length === 1 && value[0] === 'all');
  if (ALL_TARGET_SENTINEL_FIELDS.includes(field) && isAllTarget) return [];
  return [...new Set(value ?? [])];
};

export const hasUnorderedArrayFieldChanges = <T extends object>(
  obj1: T,
  obj2: T,
  fields: (keyof T)[],
): boolean =>
  fields.some((field) => {
    const a = normalizeArrayField(field as string, obj1[field] as string[] | undefined);
    const b = normalizeArrayField(field as string, obj2[field] as string[] | undefined);
    return !areArraysSameValues(a, b);
  });

const checkCampaignConditions = (campaign: Campaign, campaignCheck: Campaign): boolean => {
  // `conditions` is absent until a target is saved, so a draft that only holds the fields compared
  // below must read the same as no conditions at all.
  if (
    areObjectsDifferent(
      campaign.conditions ?? {},
      campaignCheck.conditions ?? {},
      UNORDERED_ARRAY_CONDITION_FIELDS,
    )
  )
    return true;

  return hasUnorderedArrayFieldChanges(
    campaign.conditions ?? {},
    campaignCheck.conditions ?? {},
    UNORDERED_ARRAY_CONDITION_FIELDS,
  );
};

// A price picked in the UI is built client-side -- no record id yet, no server-derived redirectURL, and a
// different key order than the fragment -- so a raw object diff flags a re-pick of the saved product as a
// change. Saving only ever creates, deletes or re-points the price record, so compare exactly that much.
const isVariantPriceDifferent = (
  price: Variant['price'],
  storedPrice: Variant['price'],
): boolean => {
  if (!price && !storedPrice) return false; // never had a product on either side
  if (!price || !storedPrice) return true; // product just picked, or removed
  return price.baseID !== storedPrice.baseID; // pointed at another product
};

// Same story as the price above: a destination picked in the UI is built client-side with no record id
// yet, and a variant just created holds no `url` key at all while the fragment reads it back as null --
// so a raw object diff flags an untouched draft, or a re-pick of the saved page, as a change. Saving only
// ever creates, deletes or re-points the destination record, which is what `determineUrlAction` reads.
const isVariantUrlDifferent = (url: Variant['url'], storedUrl: Variant['url']): boolean => {
  if (!url && !storedUrl) return false; // never had a destination on either side
  if (!url || !storedUrl) return true; // page just picked, or removed
  // The homepage, and any page picked by URL alone, carries an empty baseID, so the URL decides there.
  return url.baseID !== storedUrl.baseID || url.redirectURL !== storedUrl.redirectURL;
};

const VARIANT_IGNORED_KEYS = [
  'template',
  'themePageID',
  'updatedAt',
  'createdAt',
  'price',
  'url',
  'productVariants',
];

const checkCampaignVariants = (campaign: Campaign, campaignCheck: Campaign): boolean => {
  const contextVariants = campaign.variants ?? [];
  const storeVariants = campaignCheck.variants ?? [];
  if (contextVariants.length !== storeVariants.length) return true;

  return contextVariants.some((variant, index) => {
    const storeVariant = storeVariants[index];
    if (areObjectsDifferent(variant, storeVariant, VARIANT_IGNORED_KEYS)) return true;
    if (isVariantPriceDifferent(variant?.price, storeVariant?.price)) return true;
    if (
      isVariantProductVariantsDifferent({
        productVariants: variant?.productVariants,
        storedProductVariants: storeVariant?.productVariants,
      })
    )
      return true;
    if (isVariantUrlDifferent(variant?.url, storeVariant?.url)) return true;
    // check redirectURL when changing variant target page
    return variant?.template?.redirectURL !== storeVariant?.template?.redirectURL;
  });
};

export const isCampaignInGroupDifferent = (
  campaign: Campaign,
  campaignCheck: Campaign,
): boolean => {
  if (checkCampaignConditions(campaign, campaignCheck)) return true;

  return checkCampaignVariants(campaign, campaignCheck);
};

export const isCampaignDifferent = (campaign: Campaign, campaignCheck: Campaign): boolean => {
  if (
    areObjectsDifferent(campaign, campaignCheck, [
      'updatedAt',
      'createdAt',
      'startedAt',
      'pausedAt',
      'completedAt',
      'name',
      'status',
      'themeID',
      'versions',
      'variants',
      'conditions',
      ...UNORDERED_ARRAY_FIELDS,
    ])
  )
    return true;

  if (hasUnorderedArrayFieldChanges(campaign, campaignCheck, UNORDERED_ARRAY_FIELDS)) return true;

  if (checkCampaignConditions(campaign, campaignCheck)) return true;

  return checkCampaignVariants(campaign, campaignCheck);
};
